// =====================================================
// COMPOSANT: ProfileSection — « Profil des équipages » + « Corps de métier & études » (RUN 4)
// =====================================================
// Trois blocs, pilotés par le segment (filtre haut de page) :
//   1. Donut Féminin / Masculin / Mixte — recalculé par Segment. ⚠️ Le sexe
//      n'existe PAS en base → INFÉRÉ du prénom (étiqueté « estimation prénom »).
//   2. Caractéristiques des membres : âge moyen + tranches (N/A propre si le
//      seed n'a pas de date de naissance).
//   3. Corps de métier & études : split Étudiant / Jeune actif (fiable, en tête,
//      note Maxime) + Top 5 métiers + Top 5 types d'études (best-effort documenté).
// Recharts via ChartContainer maison, design system crm-4l (zinc). Aucune donnée mockée.

import { Info, GraduationCap, Briefcase } from 'lucide-react';
import { Cell, Pie, PieChart } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type {
  AgeProfile,
  CrewComposition,
  MetierEtudes,
  SexDistribution,
} from '@/lib/dashboard-stats';

// Rampe tonale zinc (pas de cliché rose/bleu) — cohérente avec les autres charts.
const COMP_COLORS: Record<CrewComposition, string> = {
  feminin: '#52525b', // zinc-600
  masculin: '#18181b', // zinc-900
  mixte: '#a1a1aa', // zinc-400
  indetermine: '#e4e4e7', // zinc-200
};
const COMP_LABELS: Record<CrewComposition, string> = {
  feminin: 'Féminin',
  masculin: 'Masculin',
  mixte: 'Mixte',
  indetermine: 'Indéterminé',
};
const COMP_ORDER: CrewComposition[] = ['feminin', 'masculin', 'mixte', 'indetermine'];

function pct(n: number, total: number): string {
  if (total === 0) return '—';
  return `${Math.round((n / total) * 100)}%`;
}

// ── Bloc 1 : Donut répartition des équipages ────────────────────────────────

function SexDonut({ dist }: { dist: SexDistribution }) {
  const order = COMP_ORDER;
  const data = order
    .map((k) => ({ key: k, label: COMP_LABELS[k], value: dist[k] as number }))
    .filter((d) => d.value > 0);

  const config: ChartConfig = Object.fromEntries(
    order.map((k) => [k, { label: COMP_LABELS[k], color: COMP_COLORS[k] }])
  );

  if (dist.total === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-zinc-200 text-xs text-zinc-400">
        Aucun équipage à ce stade.
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
      <ChartContainer config={config} className="aspect-square h-56">
        <PieChart>
          <ChartTooltip content={<ChartTooltipContent nameKey="label" />} />
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            innerRadius={52}
            outerRadius={82}
            paddingAngle={2}
            strokeWidth={2}
          >
            {data.map((d) => (
              <Cell key={d.key} fill={COMP_COLORS[d.key]} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>

      {/* Légende + chiffres réels */}
      <ul className="flex w-full flex-col gap-2 sm:max-w-[200px]">
        {order.map((k) => {
          const v = dist[k] as number;
          return (
            <li key={k} className="flex items-center justify-between gap-2 text-sm">
              <span className="inline-flex items-center gap-2 text-zinc-700">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                  style={{ backgroundColor: COMP_COLORS[k] }}
                />
                {COMP_LABELS[k]}
              </span>
              <span className="tabular-nums font-medium text-zinc-900">
                {v}
                <span className="ml-1 text-xs font-normal text-zinc-400">{pct(v, dist.total)}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Bloc 2 : Caractéristiques d'âge ─────────────────────────────────────────

function AgeCard({ age }: { age: AgeProfile }) {
  if (age.partial) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-extrabold leading-none text-zinc-300">N/A</span>
          <span className="text-sm text-zinc-500">âge moyen</span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Date de naissance renseignée pour {age.withBirthDate}/{age.total} membre
            {age.total > 1 ? 's' : ''} — insuffisant pour un âge fiable. Les tranches s'afficheront
            dès que la collecte progressera.
          </span>
        </div>
      </div>
    );
  }

  const maxBracket = Math.max(1, ...age.brackets.map((b) => b.count));
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-extrabold leading-none tabular-nums text-zinc-900">
          {age.averageAge}
        </span>
        <span className="text-sm text-zinc-500">
          ans en moyenne
          <span className="ml-1 text-xs text-zinc-400">({age.withBirthDate} renseignés)</span>
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {age.brackets.map((b) => (
          <div key={b.label} className="flex items-center gap-3 text-sm">
            <span className="w-12 shrink-0 text-zinc-500">{b.label}</span>
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-zinc-100">
              <div
                className="h-full rounded-full bg-zinc-800"
                style={{ width: `${(b.count / maxBracket) * 100}%` }}
              />
            </div>
            <span className="w-6 shrink-0 text-right tabular-nums font-medium text-zinc-900">
              {b.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bloc 3 : barres horizontales génériques (métiers / études) ──────────────

function HBars({
  items,
  filled,
  total,
  emptyLabel,
  icon: Icon,
}: {
  items: { label: string; count: number }[];
  filled: number;
  total: number;
  emptyLabel: string;
  icon: typeof Briefcase;
}) {
  if (items.length === 0) {
    return (
      <div className="flex h-full min-h-[6rem] items-center gap-2 rounded-lg border border-dashed border-zinc-200 px-3 py-4 text-xs text-zinc-400">
        <Icon className="h-4 w-4 shrink-0" />
        {emptyLabel}
      </div>
    );
  }
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="flex flex-col gap-2">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-3 text-sm">
          <span className="w-28 shrink-0 truncate text-zinc-700" title={it.label}>
            {it.label}
          </span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-zinc-100">
            <div
              className="h-full rounded-full bg-zinc-700"
              style={{ width: `${(it.count / max) * 100}%` }}
            />
          </div>
          <span className="w-6 shrink-0 text-right tabular-nums font-medium text-zinc-900">
            {it.count}
          </span>
        </div>
      ))}
      <span className="mt-1 text-[11px] text-zinc-400">
        {filled}/{total} membre{total > 1 ? 's' : ''} renseigné{filled > 1 ? 's' : ''}
      </span>
    </div>
  );
}

// ── Split Étudiant / Jeune actif (fiable — en tête, note Maxime) ────────────

function StudentSplitBar({ me }: { me: MetierEtudes }) {
  const { studentSplit: s } = me;
  const known = s.etudiant + s.jeuneActif;
  const segs = [
    { key: 'etudiant', label: 'Étudiants', value: s.etudiant, color: '#18181b' },
    { key: 'jeuneActif', label: 'Jeunes actifs', value: s.jeuneActif, color: '#a1a1aa' },
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums text-zinc-900">{s.etudiant}</span>
        <span className="text-sm text-zinc-500">
          étudiant{s.etudiant > 1 ? 's' : ''} · {s.jeuneActif} jeune
          {s.jeuneActif > 1 ? 's' : ''} actif{s.jeuneActif > 1 ? 's' : ''}
        </span>
      </div>
      {known > 0 ? (
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-zinc-100">
          {segs.map((seg) =>
            seg.value > 0 ? (
              <div
                key={seg.key}
                style={{ width: `${(seg.value / known) * 100}%`, backgroundColor: seg.color }}
                title={`${seg.label} : ${seg.value}`}
              />
            ) : null
          )}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-4 text-xs text-zinc-600">
        {segs.map((seg) => (
          <span key={seg.key} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: seg.color }} />
            {seg.label} · <span className="font-medium text-zinc-900">{seg.value}</span>
          </span>
        ))}
        {s.inconnu > 0 ? <span className="text-zinc-400">{s.inconnu} non renseigné</span> : null}
      </div>
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────────────────

interface ProfileSectionProps {
  sex: SexDistribution;
  age: AgeProfile;
  metierEtudes: MetierEtudes;
}

export function ProfileSection({ sex, age, metierEtudes }: ProfileSectionProps) {
  return (
    <section className="flex flex-col gap-6">
      {/* Titre de section */}
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Profil des équipages
        </h2>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Donut répartition F/M/Mixte */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-4 px-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-zinc-950">Répartition des équipages</h3>
                <span className="text-xs text-zinc-500">
                  Équipages entrés sur la plage · vue globale
                </span>
              </div>
              <span
                className="inline-flex items-center gap-1 rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500"
                title="Le sexe est déclaré par le participant dans le BED (donnée réelle)."
              >
                <Info className="h-3 w-3" />
                source&nbsp;: BED
              </span>
            </div>
            <SexDonut dist={sex} />
            {sex.indetermine > 0 ? (
              <p className="text-[11px] leading-relaxed text-zinc-400">
                {sex.indetermine} équipage{sex.indetermine > 1 ? 's' : ''} « indéterminé » : au
                moins un membre dont le sexe n'est pas encore renseigné dans le BED. La donnée se
                complète à mesure que les bulletins sont remplis.
              </p>
            ) : null}
          </CardContent>
        </Card>

        {/* Caractéristiques d'âge */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-4 px-6">
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-zinc-950">Âge des membres</h3>
              <span className="text-xs text-zinc-500">Moyenne + répartition par tranche d'âge</span>
            </div>
            <AgeCard age={age} />
          </CardContent>
        </Card>
      </div>

      {/* Corps de métier & études */}
      <div className="flex items-baseline gap-3 pt-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Corps de métier &amp; études
        </h2>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Split étudiant / actif — FIABLE, en tête (note Maxime) */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-3 px-6">
            <h3 className="text-sm font-semibold text-zinc-950">Étudiants / Jeunes actifs</h3>
            <StudentSplitBar me={metierEtudes} />
          </CardContent>
        </Card>

        {/* Top 5 métiers — best-effort */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-3 px-6">
            <h3 className="text-sm font-semibold text-zinc-950">Top corps de métier</h3>
            <HBars
              items={metierEtudes.metiers}
              filled={metierEtudes.metiersRenseignes}
              total={metierEtudes.total}
              emptyLabel="Corps de métier non renseignés dans les fiches (champ libre)."
              icon={Briefcase}
            />
          </CardContent>
        </Card>

        {/* Top 5 types d'études — best-effort */}
        <Card className="border border-zinc-200">
          <CardContent className="flex flex-col gap-3 px-6">
            <h3 className="text-sm font-semibold text-zinc-950">Top types d'études</h3>
            <HBars
              items={metierEtudes.etudes}
              filled={metierEtudes.etudesRenseignees}
              total={metierEtudes.total}
              emptyLabel="Aucune école rattachée aux membres (type d'études indisponible)."
              icon={GraduationCap}
            />
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
